import React, {Dispatch, FunctionComponent, KeyboardEvent, SetStateAction, useCallback, useMemo, useState} from "react";
import {Box, DialogActions, DialogContent, TextField, Tooltip, Typography} from "@mui/material";
import Button from "../../../../../../components/base/button/button";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import LoadingLottieAnimation from "../../../../../../components/app-specific/lottie-animations/loading";
import ToastService from "../../../../../../../core/services/toast";
import {DataGridColumn, DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, DataGridRow, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import {StandaloneCheckbox} from "../../../../../../components/base/checkbox/checkbox";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import BizProductionApiService, {ApiServiceModels} from "../../../../../../../core/services/api_service/production_api_service";
import {PurchasePlanningInformationImportDialogDataEntry} from "./index";
import Utils from "../../../../../../../core/services/utils/utils";
import {Tab, Tabs} from "../../../../../../components/base/Tabs";
import ProductInformationPopover from "../../../../../../components/app-specific/draggable-popover/product-information-popover";
import useAccessLevels from "../../../../../../hooks/use-access-levels";


export interface PrefixDataRecord extends ApiServiceModels.GetPurchasePlanningPartsByPrefixResponseDs {
    qty: string;
    note?: string;
}

interface PrefixData {
    id: string;
    prefix: string;
    products: PrefixDataRecord[];
    selection: number[];
    importing: boolean;
}

export interface PurchasePlanningInformationImportDialogPrefixImportProps {
    onImport: (data: PurchasePlanningInformationImportDialogDataEntry[]) => void | Promise<void>;
    closeDialog: () => void;
}


const PurchasePlanningInformationImportDialogPrefixImport: FunctionComponent<PurchasePlanningInformationImportDialogPrefixImportProps> = (props) => {
    const {onImport} = props;
    const [newPrefix, setNewPrefix] = useState<string>('');
    const [prefixList, setPrefixList] = useState<PrefixData[]>([]);
    const [selectedPrefixId, setSelectedPrefixId] = useState<PrefixData['id']>();
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const canExportPlanning = packages.purchasing.exportPurchasePlanning;

    const anyPrefixImporting = useMemo(() => prefixList.some(e => e.importing), [prefixList]);
    const selectedPrefix = useMemo(() => prefixList.find(e => e.id === selectedPrefixId), [prefixList, selectedPrefixId]);
    const setSelectedPrefix = useCallback<Dispatch<SetStateAction<PrefixData>>>((setState) => {
        setPrefixList((prev) => prev.map(e => e.id === selectedPrefixId ? {
            ...e,
            ...(typeof setState === 'function' ? setState(e) : setState),
        } : e));
    }, [selectedPrefixId]);


    /**
     * Fetch products based on the newPrefix entered by the user.
     */
    const fetchProductsViaPrefix = useCallback(async () => {
        const prefixTrimmed = newPrefix.trim().toUpperCase();
        if (prefixTrimmed.length === 0) {
            ToastService.error({message: "Prefix cannot be empty.", title: "Invalid Prefix"});
            return;
        }
        const prefixData = {
            id: Utils.createUUID(),
            prefix: prefixTrimmed,
            products: [],
            selection: [],
            importing: true,
        }
        setPrefixList(prevState => [...prevState, prefixData])
        const response = await BizProductionApiService.v1.purchasePlanning.getPartsByPrefix({
            prefix: prefixData.prefix,
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setPrefixList(prevState => prevState.map(e => e.id === prefixData.id ? {
                ...e,
                importing: false,
                products: response.data?.map(e => ({...e, qty: '1', note: ''})) ?? [],
                selection: response.data?.map(e => e.id) ?? [],
            } : e));
            setSelectedPrefixId(prefixData.id);
            setNewPrefix('');
        } else {
            setPrefixList(prevState => prevState.filter(e => e.id !== prefixData.id));
        }
    }, [isMounted, newPrefix])

    /**
     * Callback function passed to the column mapper.
     */
    const importSelection = useCallback(() => {
        const selectedProducts = selectedPrefix?.products
            .filter(product => selectedPrefix?.selection.includes(product.id) ?? false)
            .map(e => ({...e, quantity: Number.isNaN(Number(e.qty)) ? 0 : Number(e.qty)}))
            .filter(e => e.quantity > 0) ?? [];
        if (selectedProducts.length === 0) {
            ToastService.error({message: "Please select at least one product with quantity greater than zero.", title: "No Products Selected"});
            return;
        }
        onImport?.(selectedProducts);
    }, [onImport, selectedPrefix?.products, selectedPrefix?.selection])

    /**
     * Export the current selection as a CSV file.
     */
    const exportSelection = useCallback(() => {
        const selectedProducts = selectedPrefix?.products
            .filter(product => selectedPrefix?.selection.includes(product.id) ?? false)
            .map(e => ({...e, qty: Number.isNaN(Number(e.qty)) ? 0 : Number(e.qty)}))
            .filter(e => e.qty > 0) ?? [];
        if (selectedProducts.length === 0) {
            ToastService.error({message: "Please select at least one product with quantity greater than zero.", title: "No Products Selected"});
            return;
        }
        const csvContent = selectedProducts.map(e => ({
            "Part No": e.partNo,
            "Qty": e.qty,
            "Note": e.note ?? '',
        }));
        Utils.exportCsvFile(csvContent, {fileTitle: `purchase-planning-export-prefix-${selectedPrefix?.prefix ?? 'selection'}`});
    }, [selectedPrefix?.prefix, selectedPrefix?.products, selectedPrefix?.selection])

    /**
     * If the user presses enter, and the new option is enabled, then add the next option.
     */
    const onFormKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') {
            return;
        }
        void fetchProductsViaPrefix();
    }, [fetchProductsViaPrefix])

    return (
        <>
            <DialogContent>
                <Box sx={{display: 'flex', alignItems: 'center', flexDirection: 'column', width: '100%', gap: 2}}>
                    <TextField
                        fullWidth
                        label={'Prefix'}
                        size={'small'}
                        placeholder={'Enter Prefix to search products'}
                        value={newPrefix}
                        onChange={(e) => setNewPrefix(e.target.value?.toUpperCase() ?? "")}
                        disabled={anyPrefixImporting}
                        onKeyDown={onFormKeyDown}
                    />
                    {
                        prefixList.length > 0 &&
                        <Tabs className={'d-flex w-100'} value={selectedPrefixId ?? false} scrollButtons={false} onChange={(_, newValue) => setSelectedPrefixId(newValue)}>
                            {
                                prefixList.map((prefix) => (
                                    <Tab
                                        key={prefix.id}
                                        value={prefix.id}
                                        label={`${prefix.prefix} ${prefix.importing ? '(loading...)' : `(${prefix.products.length} products)`}`}
                                    />
                                ))
                            }
                        </Tabs>
                    }
                    {
                        !selectedPrefix &&
                        <Typography variant={'body2'} fontWeight={500} sx={{marginBlock: 4}}>
                            Please enter a prefix to search for products.
                        </Typography>
                    }
                    {
                        selectedPrefix &&
                        <PrefixInformation
                            data={selectedPrefix}
                            onChange={setSelectedPrefix}
                        />
                    }
                </Box>
            </DialogContent>
            <DialogActions sx={{display: 'flex', alignItems: 'center', p: 0, m: 0}}>
                <Button
                    variant={'dialog'}
                    size={'medium'}
                    type={'button'}
                    color={'primary'}
                    borderRadius={'0 8px 0 8px'}
                    onClick={props.closeDialog}
                >
                    Close
                </Button>
                <div className={'ml-auto'}/>
                {
                    prefixList.length > 0 && !!selectedPrefix && selectedPrefix.selection.length > 0 &&
                    <Tooltip title={`Imports the currently selected prefix's selection (${selectedPrefix.prefix}). If you want to import other prefixes, please select them from the tabs above.`}>
                        <Button
                            variant={'dialog'}
                            size={'medium'}
                            color={'primary'}
                            type={'submit'}
                            borderRadius={'8px 0 8px 0'}
                            onClick={importSelection}
                            disabled={selectedPrefix?.selection.length === 0}
                        >
                            Import Loaded Products ({selectedPrefix?.selection.length} parts)
                        </Button>
                    </Tooltip>
                }
                {
                    canExportPlanning && prefixList.length > 0 && !!selectedPrefix && selectedPrefix.selection.length > 0 &&
                    <Tooltip
                        title={`Exports the currently selected prefix's selection (${selectedPrefix.prefix}) as a CSV file. If you want to export other prefixes, please select them from the tabs above.`}>
                        <Button
                            variant={'dialog'}
                            size={'medium'}
                            color={'primary'}
                            type={'submit'}
                            borderRadius={'8px 0 8px 0'}
                            onClick={exportSelection}
                            disabled={selectedPrefix?.selection.length === 0}
                        >
                            Export as CSV ({selectedPrefix?.selection.length} parts)
                        </Button>
                    </Tooltip>
                }
            </DialogActions>
        </>
    );
}


const dataGridColumnsNames = {
    selection: 'selection',
    partNo: 'partNo',
    quantity: 'quantity',
    note: 'note',
}

const dataGridSaveConfig = {
    name: 'PurchasePlanningImportWithPrefixDG',
    version: 1,
}

interface PrefixInformationProps {
    data: PrefixData;
    onChange: Dispatch<SetStateAction<PrefixData>>
}

const PrefixInformation: FunctionComponent<PrefixInformationProps> = (props) => {
    const {onChange: onChangeProps} = props;

    const products = useMemo(() => props.data.products, [props.data.products]);
    const setProducts = useCallback<Dispatch<SetStateAction<any[]>>>((setState) => {
        onChangeProps((prev) => ({
            ...prev,
            products: typeof setState === 'function' ? setState(prev.products) : setState,
        }));
    }, [onChangeProps])

    const selection = useMemo(() => props.data.selection, [props.data.selection]);
    const setSelection = useCallback<Dispatch<SetStateAction<number[]>>>((setState) => {
        onChangeProps((prev) => ({
            ...prev,
            selection: typeof setState === 'function' ? setState(prev.selection) : setState,
        }));
    }, [onChangeProps])

    const columns = useMemo<DataGridColumn[]>(() => [
        {
            name: dataGridColumnsNames.selection,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.center,
            sortable: false,
            width: 0,
            resizable: false,
            reorderable: false,
            visibilityToggleable: false,
            pinnedToggleable: false,
            pinned: true,
        },
        {
            name: dataGridColumnsNames.partNo,
            title: 'Part No',
            type: DataGridColumnTypes.string,
            width: 150,
            sortable: false,
        },
        {
            name: dataGridColumnsNames.quantity,
            title: 'Quantity',
            type: DataGridColumnTypes.element,
            width: 100,
            sortable: false,
        },
        {
            name: dataGridColumnsNames.note,
            title: 'Note',
            type: DataGridColumnTypes.string,
            width: {type: DataGridColumnWidthTypes.flex, size: 1},
            sortable: false,
        }
    ] as Partial<DataGridColumn>[] as unknown as DataGridColumn[], [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });

    /**
     * Change the selection of a product.
     */
    const changeSelection = useCallback((productId: PrefixDataRecord['id'], checked: boolean) => {
        setSelection(prev => {
            if (checked) {
                return [...prev, productId];
            } else {
                return prev.filter(id => id !== productId);
            }
        })
    }, [setSelection]);

    /**
     * When the quantity field is changed, update the quantity in the products state.
     */
    const onQuantityChanged = useCallback((productId: PrefixDataRecord['id'], newQty: string) => {
        setProducts((prev) => prev.map(p => p.id === productId ? {...p, qty: newQty} : p));
    }, [setProducts]);

    /**
     * When the quantity field is blurred, ensure that the quantity is a valid number and not negative. If invalid, set it to 0.
     */
    const onQuantityBlurred = useCallback((productId: PrefixDataRecord['id'], newQty: string) => {
        const qtyNumber = Number(newQty);
        setProducts((prev) => prev.map(p => p.id === productId ? {...p, qty: !Number.isNaN(qtyNumber) && qtyNumber >= 0 ? qtyNumber : 0} : p));
    }, [setProducts]);

    /**
     * When the note field is changed, update the note in the products state.
     */
    const onNoteChanged = useCallback((productId: PrefixDataRecord['id'], newNote?: string) => {
        setProducts((prev) => prev.map(p => p.id === productId ? {...p, note: newNote ?? ""} : p));
    }, [setProducts]);

    const dataGridRows = useMemo<DataGridRow[]>(() => {
        return products.map((product) => {
            const selectionChecked = selection.includes(product.id);
            return {
                key: product.id,
                data: product,
                cells: {
                    [dataGridColumnsNames.selection]:
                        <StandaloneCheckbox
                            checked={selectionChecked}
                            onChange={(_, checked) => changeSelection(product.id, checked)}
                        />,
                    [dataGridColumnsNames.partNo]:
                        <ProductInformationPopover partNo={product.partNo}>
                            {product.partNo ?? ''}
                        </ProductInformationPopover>,
                    [dataGridColumnsNames.quantity]:
                        <TextField
                            size={'small'}
                            type={'number'}
                            value={product.qty}
                            onChange={(e) => onQuantityChanged(product.id, e.target.value ?? "")}
                            onBlur={(e) => onQuantityBlurred(product.id, e.target.value ?? "")}
                        />,
                    [dataGridColumnsNames.note]:
                        <TextField
                            size={'small'}
                            type={'text'}
                            value={product.note ?? ''}
                            onChange={(e) => onNoteChanged(product.id, e.target.value ?? "")}
                            placeholder={'note for this product'}
                        />,
                },
            } satisfies DataGridRow;
        });
    }, [products, selection, changeSelection, onQuantityChanged, onQuantityBlurred, onNoteChanged])

    return (
        <>
            {
                props.data.importing &&
                <>
                    <LoadingLottieAnimation width={'80%'} className={''}/>
                </>
            }
            {
                !props.data.importing &&
                <>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, width: '100%'}}>
                        <Typography variant={'body2'} marginBlock={2}>
                            Select Products for: {props.data.prefix}. Total products loaded: {products.length}. please note that you can import these selected products by clicking the "Import Loaded
                            Products" button below or export them as a CSV file by clicking the "Export as CSV" button.
                        </Typography>
                    </Box>
                    <PartnerDataGrid
                        classNames={{toolbar: "pt-0"}}
                        visibleRows={5}
                        {...dataGridSaveConfig}
                        columns={dataGridColumns}
                        initialLayoutState={{columns: columns}}
                        rows={dataGridRows}
                        useVirtualizedRows
                        state={{
                            pagination: {pageSize: products.length, currentPage: 1, length: products.length},
                            loading: {state: false},
                            density: dataGridDensity,
                        }}
                        hideFooter
                        hideExporting
                    />
                </>
            }
        </>
    );
}

export default PurchasePlanningInformationImportDialogPrefixImport;